import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceDot,
} from 'recharts';
import './style.scss';
import { getMonthName } from 'src/onboarding/helpers/getMonthName';
import { getFullMonthName } from 'src/onboarding/helpers/getFullMonthName';
import { colors } from 'src/styles/colors';
import { t } from '@web-solutions/module-localization';
import { getAdjustedDate } from 'src/onboarding/helpers/getAdjustedDate';

const CustomDot = (props) => {
  const { cx, cy, payload, unit, weight, showNewPointText, date, difference, index } = props;

  if (payload.weight2 == null || index !== 2) return null;

  return (
    <>
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={9.7}
          fill={colors.baseGreen}
          stroke={colors.baseGreen}
          strokeWidth={2}
        />
        <rect
          x={cx - 50}
          y={cy - 45}
          width={100}
          height={24}
          rx={6}
          ry={6}
          fill={colors.orange}
          opacity={showNewPointText ? 1 : 0}
        />
        <text
          x={cx}
          y={cy - 28}
          textAnchor='middle'
          fill='white'
          fontSize='11'
          fontFamily='Inter'
          fontWeight='500'
          opacity={showNewPointText ? 1 : 0}
        >
          {weight} {unit} in {date}
        </text>
      </g>
    </>
  );
};

const CustomTick =
  (showNewPointText) =>
  ({ x, y, payload, index }) => {
    let dx = 0;

    if (index === 0) {
      dx = 15;
    }

    if (index === 2) {
      dx = -25;
    }
    let fill = colors.baseGray3;

    if (index === 2) {
      fill = showNewPointText ? colors.baseGray3 : colors.baseGray4;
    } else if (index === 3) {
      fill = showNewPointText ? colors.baseGray4 : colors.baseGray3;
    }

    return (
      <text
        x={x + dx}
        y={y + 10}
        textAnchor='middle'
        fontSize={15}
        fontFamily='Inter'
        fontWeight='600'
        fill={fill}
      >
        {payload.value}
      </text>
    );
  };

const GoalGraph = ({ weightNow, targetDate, targetWeight, unit, differenceDate, styles }) => {
  const [showSecondPoint, setShowSecondPoint] = useState(false);
  const [showNewPointText, setShowNewPointText] = useState(false);

  const [highlightLastLine, setHighlightLastLine] = useState(false);
  const [showGradientLine, setShowGradientLine] = useState(false);

  const animationDuration = 1600;

  const startValue = weightNow;
  const endValue = targetWeight;
  const difference = startValue - endValue;

  const minWeight = Math.min(startValue, endValue);
  const maxWeight = Math.max(startValue, endValue);

  const verticalPaddingPercent = 0.38;

  const range = (maxWeight - minWeight) / (1 - verticalPaddingPercent);
  const minY = minWeight - range * verticalPaddingPercent;
  const maxY = minY + range;

  const midY = (minY + maxY) / 2;

  const adjustedDate = getAdjustedDate(targetDate, differenceDate);

  const weightData = [
    { date: t('onboarding.goal.now'), weight: weightNow, weight2: weightNow },
    { date: '  ', weight: null, weight2: null },
    { date: adjustedDate.label, weight: midY, weight2: targetWeight },
    {
      date: `${getMonthName(targetDate.month)} ${targetDate.year}`,
      weight: targetWeight,
      weight2: null,
    },
  ];

  useEffect(() => {
    const timeout1 = setTimeout(() => {
      setShowSecondPoint(true);
      setHighlightLastLine(true);
      setShowGradientLine(true);
    }, animationDuration);

    const timeout2 = setTimeout(() => {
      setShowNewPointText(true);
    }, animationDuration + 2000);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
    };
  }, []);

  return (
    <div
      className='containerGraph'
      style={
        styles && {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: '30px',
          marginBottom: '20px'
        }
      }
    >
      <ResponsiveContainer width='100%' height={200}>
        <LineChart data={weightData}>
          <XAxis
            dataKey='date'
            tick={CustomTick(showNewPointText)}
            tickLine={false}
            strokeWidth='1'
            stroke={colors.gray}
          />

          <YAxis
            tick={false}
            stroke={difference < 1 && difference >= 0 ? colors.baseGreen : colors.orange}
            strokeWidth='1'
            strokeDasharray='10 10'
            domain={[minY, maxY]}
          />

          <CartesianGrid vertical={false} stroke={colors.gray} strokeWidth={2} />

          <line
            x1='18%'
            y1={60}
            x2='100%'
            y2={60}
            stroke={colors.baseGray6}
            strokeWidth='1'
            opacity='0.3'
            strokeDasharray='3 3'
          />
          <line
            x1='18%'
            y1={110}
            x2='100%'
            y2={110}
            stroke={colors.baseGray6}
            opacity='0.3'
            strokeWidth='1'
            strokeDasharray='3 3'
          />
          <line
            x1='18%'
            y1={24}
            x2='100%'
            y2={24}
            stroke={colors.baseGray6}
            strokeWidth='1'
            opacity='0.3'
            strokeDasharray='3 3'
          />
          {[0.43, 0.715, 0.987].map((pos, i) => {
            const isLast = i === 2;
            const isMiddle = i === 1;

            let strokeColor = colors.baseGray;

            if (isLast) {
              if (showNewPointText) {
                strokeColor = colors.baseGray;
              } else if (highlightLastLine) {
                strokeColor = colors.baseGreen;
              } else {
                strokeColor = colors.baseGray;
              }
            } else if (isMiddle) {
              if (showNewPointText) {
                strokeColor = colors.baseGreen;
              } else {
                strokeColor = difference < 1 && difference >= 0 ? colors.baseGray : colors.orange;
              }
            } else {
              strokeColor = difference < 1 && difference >= 0 ? colors.baseGray : colors.orange;
            }

            return (
              <line
                key={i}
                x1={`${pos * 100}%`}
                y1='10'
                x2={`${pos * 100}%`}
                y2='160'
                stroke={strokeColor}
                strokeWidth='1'
                strokeDasharray='10 10'
                className='fade-line'
              />
            );
          })}

          <Line
            type='bump'
            dataKey='weight'
            strokeWidth={3}
            stroke={colors.gray}
            dot={false}
            animationEasing='linear'
            animationBegin={0}
            animationDuration={animationDuration}
            connectNulls
          />
          {showGradientLine && (
            <Line
              type='bump'
              dataKey='weight2'
              strokeWidth={3}
              stroke='url(#gradient)'
              dot={(props) => (
                <CustomDot
                  {...props}
                  weight={targetWeight}
                  unit={unit}
                  showNewPointText={showNewPointText}
                  date={getFullMonthName(adjustedDate.month)}
                  difference={difference}
                  index={props.index}
                />
              )}
              animationEasing='linear'
              animationBegin={0}
              animationDuration={animationDuration}
              connectNulls
            />
          )}

          <ReferenceDot
            x={t('onboarding.goal.now')}
            y={weightNow}
            r={9.7}
            fill={colors.orange}
            stroke={colors.orange}
            strokeWidth={2}
            isFront
            label={{
              value: `${weightNow} ${unit}`,
              position: 'top',
              fill: 'black',
              fontSize: 15,
              fontWeight: 600,
              fontFamily: 'Inter',
              dy: -10,
            }}
          />

          {showSecondPoint && (
            <ReferenceDot
              x={weightData[3].date}
              y={targetWeight}
              r={0}
              fill={colors.baseGreen}
              stroke={colors.baseGreen}
              strokeWidth={2}
              isFront
              label={{
                value: `${targetWeight} ${unit}`,
                position: 'top',
                fill: 'black',
                fontSize: 15,
                fontWeight: 600,
                fontFamily: 'Inter',
                dy: -10,
                dx: -20,
              }}
            />
          )}

          <defs>
            <linearGradient id='gradient' x1='0' y1='0' x2='1' y2='0'>
              <stop offset='0%' stopColor={colors.orange} />
              <stop offset='60%' stopColor={colors.yellow} />
              <stop offset='100%' stopColor={colors.baseGreen} />
            </linearGradient>
          </defs>
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default GoalGraph;
